<template>
  <div class="w-full max-w-md mx-auto px-4 py-6 space-y-6">
    <!-- Daily Diary Card -->
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 mt-4 overflow-hidden">
      <div class="bg-gradient-to-r from-ccm-blue-100 to-ccm-blue-200 px-4 py-3">
        <div class="flex items-center justify-between">
          <h1 class="font-semibold text-white text-xl">
            {{ $t('Mobile.MobileDiaryIndex.dailyDiary') }}
          </h1>

          <div class="w-8 h-8 bg-white/20 rounded-full flex items-center justify-center">
            <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-5 h-5 text-white">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>
          </div>
        </div>
      </div>

      <div class="p-4 space-y-4">
        <!-- Add Entry Button -->
        <RouterLink
          v-if="userStore.hasAccessToRoute('MobileDiaryEntry') && projectsStore.project?.unarchived"
          :to="{ name: 'MobileDiaryEntry' }"
          class="btn btn--primary-blue shadow-md w-full flex items-center justify-center space-x-2"
          data-test="new-entry"
        >
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5">
  <path fill-rule="evenodd" d="M12 3.75a.75.75 0 01.75.75v6.75h6.75a.75.75 0 010 1.5h-6.75v6.75a.75.75 0 01-1.5 0v-6.75H4.5a.75.75 0 010-1.5h6.75V4.5a.75.75 0 01.75-.75z" clip-rule="evenodd" />
</svg>

          <span>{{ $t('Mobile.MobileDiaryIndex.addEntry') }}</span>
        </RouterLink>

        <!-- Diary Entries Section -->
        <div class="space-y-3">
          <AppSpinner
            v-if="fetchDiaryEntriesAction.is(States.LOADING)"
            class="p-4 mx-auto"
          />

          <div
            v-else-if="diaryEntries.length > 0"
            class="space-y-3"
          >
            <div
              v-for="diaryEntry in diaryEntries"
              :key="diaryEntry.id"
              class="bg-gray-50 rounded-lg border border-gray-200 overflow-hidden hover:shadow-md transition-shadow duration-200"
            >
              <RouterLink
                :to="{ name: 'MobileDiaryEntry', params: { diaryId: diaryEntry.id } }"
                class="block p-4"
              >
                <div class="flex items-start justify-between">
                  <div class="flex-1 min-w-0">
                    <div class="flex items-center space-x-2 mb-2">
                      <div class="flex items-center space-x-1 text-sm text-gray-600">
                        <svg
    fill="none"
    stroke="currentColor"
    viewBox="0 0 24 24"
 class="w-4 h-4">
    <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
    />
</svg>

                        <span class="font-medium">{{ diaryEntry.createdAt.toLocaleString() }}</span>
                      </div>
                    </div>

                    <div
                      v-if="!userIsSiteManager && diaryEntry.user_id !== userStore.user.id"
                      class="flex items-center space-x-1 text-sm text-blue-600 mb-2"
                    >
                      <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="w-4 h-4">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.75 6a3.75 3.75 0 1 1-7.5 0 3.75 3.75 0 0 1 7.5 0ZM4.501 20.118a7.5 7.5 0 0 1 14.998 0A17.933 17.933 0 0 1 12 21.75c-2.676 0-5.216-.584-7.499-1.632Z" />
</svg>

                      <span>{{ diaryEntry.user.name }}</span>
                    </div>

                    <p class="text-gray-900 line-clamp-3 leading-relaxed">
                      {{ diaryEntry.body }}
                    </p>
                  </div>

                  <div class="flex-shrink-0 my-auto">
                    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-gray-400">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
                  </div>
                </div>
              </RouterLink>
            </div>
          </div>

          <div
            v-else
            class="text-center py-8"
          >
            <div class="flex flex-col items-center space-y-3">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" stroke="currentColor" viewBox="0 0 24 24"  class="w-12 h-12 text-gray-300">
    <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
</svg>

              <div class="text-gray-500">
                <p class="font-medium">
                  {{ $t('Mobile.MobileDiaryIndex.noEntriesFound') }}
                </p>

                <p class="text-sm">
                  {{ $t('Mobile.MobileDiaryIndex.createFirstEntry') }}
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Date Navigation -->
        <div class="bg-gray-50 rounded-lg p-4 border border-gray-200">
          <div class="flex items-center justify-between space-x-3">
            <button
              type="button"
              class="w-10 h-10 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors duration-200 shadow-sm"
              data-test="prev-day"
              @click="prevDate"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-gray-600">
  <path fill-rule="evenodd" d="M7.72 12.53a.75.75 0 010-1.06l7.5-7.5a.75.75 0 111.06 1.06L9.31 12l6.97 6.97a.75.75 0 11-1.06 1.06l-7.5-7.5z" clip-rule="evenodd" />
</svg>
            </button>

            <AppInput
              v-model="selectedDay"
              class="form-input"
              type="date"
              name="selected_date"
              data-test="select-day-input"
            />

            <button
              type="button"
              class="w-10 h-10 bg-white border border-gray-300 rounded-full flex items-center justify-center hover:bg-gray-50 transition-colors duration-200 shadow-sm"
              data-test="next-day"
              @click="nextDate"
            >
              <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="currentColor" class="w-5 h-5 text-gray-600">
  <path fill-rule="evenodd" d="M16.28 11.47a.75.75 0 010 1.06l-7.5 7.5a.75.75 0 01-1.06-1.06L14.69 12 7.72 5.03a.75.75 0 011.06-1.06l7.5 7.5z" clip-rule="evenodd" />
</svg>
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import useDayQueryParamRoute from '@/composables/useDayQueryParamRoute';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { t } from '@/i18n';
import { useDiaryEntriesStore } from '@/store/diaryEntries';
import { useNavStore } from '@/store/nav';
import { useProjectsStore } from '@/store/projects';
import { useUserStore } from '@/store/user';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';

const props = defineProps<{
  projectId: string;
}>();

const userStore = useUserStore();
const diaryEntriesStore = useDiaryEntriesStore();
const fetchDiaryEntriesAction = useStoreApiAction(diaryEntriesStore.fetchDiaryEntries);
const { day: selectedDay, nextDate, prevDate } = useDayQueryParamRoute();

const diaryEntries = computed(() => {
  return diaryEntriesStore.models.where('project_id', parseInt(props.projectId)).with('user').where(
    'created_at',
    (created_at) => {
      return created_at.split('T')[0] === selectedDay.value;
    },
  ).orderBy(
    'created_at',
    'desc',
  ).get();
});

watch(selectedDay, (newValue) => {
  fetchDiaryEntriesAction.request(props.projectId, { date: newValue }).then((entries) => {
    console.log(entries);
  }).catch((error) => {
    if(error?.response.status === 500 || error?.response.status === 403) {
      Swal.fire({
        icon: 'error',
        text: t('Mobile.MobileDiaryIndex.fetchEntriesError'),
      });
    }
  });
}, { immediate: true });

const navStore = useNavStore();

navStore.backRoute = { name: 'MobileProjectPage' };

const userIsSiteManager = computed(() => {
  return userStore.isRole(['site_manager'], false);
});

const projectsStore = useProjectsStore();
</script>

<style scoped></style>
